import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase.js'
import { apiFetch } from '../lib/api.js'
import { GAME_DEFAULTS, VEHICLE_TYPES, MIN_CIRCUIT_CASES, MAX_CIRCUIT_CASES, BONUS_RATIO } from '../lib/constants.js'
import { pageBg as podiumBg } from '../lib/images'
import { TwemojiImg } from '../lib/twemoji.jsx'
import { saveSession } from '../lib/session.js'
import FeedbackForm from '../components/ui/FeedbackForm.jsx'
import WelcomeVideoCard from '../components/ui/WelcomeVideoCard.jsx'
import { useContentPrefs } from '../hooks/useContentPrefs.js'

// ── Choix du deck (dark) ─────────────────────────────────────
function StepDeck({ selected, onSelect, showChevron = false }) {
  const [tab, setTab] = useState('mine')
  const [search, setSearch] = useState('')
  const [eduFilter, setEduFilter] = useState('')
  const [matiereFilter, setMatiereFilter] = useState('')
  const [tabDecks, setTabDecks] = useState([])
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const data = await apiFetch(`/api/decks?tab=${tab}`)
        if (cancelled) return
        if (tab === 'mine' && data.length === 0 && !initialized) {
          setTab('community')
          return
        }
        setTabDecks(data)
        setInitialized(true)
      } catch {
        if (!cancelled) setTabDecks([])
      }
      if (!cancelled) setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  const s = search.toLowerCase()
  const filtered = tabDecks
    .filter(d => !s || d.titre.toLowerCase().includes(s) || d.keywords?.some(k => k.toLowerCase().includes(s)))
    .filter(d => !eduFilter || d.education_level === eduFilter)
    .filter(d => !matiereFilter || d.matiere === matiereFilter)

  const eduLevels = [...new Set(tabDecks.map(d => d.education_level).filter(Boolean))]
  const matieres = [...new Set(tabDecks.map(d => d.matiere).filter(Boolean))]

  function switchTab(t) {
    setTab(t)
    setSearch('')
    setEduFilter('')
    setMatiereFilter('')
  }

  return (
    <div className="space-y-3">
      {/* Onglets */}
      <div className="flex gap-1 bg-white/10 rounded-xl p-1">
        {[{ key: 'mine', label: 'Mes quiz' }, { key: 'community', label: 'Communauté' }].map(t => (
          <button
            key={t.key}
            type="button"
            onClick={() => switchTab(t.key)}
            className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium transition ${
              tab === t.key ? 'bg-white/20 text-white' : 'text-white/50 hover:text-white/80'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Recherche */}
      <input
        type="text"
        placeholder="Rechercher un quiz…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
      />

      {/* Chips niveau */}
      {eduLevels.length > 0 && (
        <div className="chips-scroll flex gap-1.5 overflow-x-auto whitespace-nowrap pb-2">
          <button
            type="button"
            onClick={() => setEduFilter('')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition shrink-0 ${
              !eduFilter ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            Tous
          </button>
          {eduLevels.map(lvl => (
            <button
              key={lvl}
              type="button"
              onClick={() => setEduFilter(eduFilter === lvl ? '' : lvl)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition shrink-0 ${
                eduFilter === lvl ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
            >
              {lvl}
            </button>
          ))}
        </div>
      )}

      {/* Chips matière */}
      {matieres.length > 0 && (
        <div className="chips-scroll flex gap-1.5 overflow-x-auto whitespace-nowrap pb-2">
          <button
            type="button"
            onClick={() => setMatiereFilter('')}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition shrink-0 ${
              !matiereFilter ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            Toutes
          </button>
          {matieres.map(mat => (
            <button
              key={mat}
              type="button"
              onClick={() => setMatiereFilter(matiereFilter === mat ? '' : mat)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition shrink-0 ${
                matiereFilter === mat ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
            >
              {mat}
            </button>
          ))}
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="py-8 text-center text-white/40 text-sm">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-white/40">
          <div className="text-3xl mb-2">📚</div>
          <p className="font-medium text-sm">
            {tab === 'mine' ? 'Aucun quiz personnel.' : 'Aucun quiz disponible.'}
          </p>
          {tab === 'mine' && (
            <p className="text-xs mt-1">
              <Link to="/admin/decks" className="text-primary hover:underline">Crée un quiz</Link> dans l'espace Admin.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(deck => (
            <div
              key={deck.id}
              className={`flex items-stretch rounded-xl border-2 transition group/item ${
                selected?.id === deck.id
                  ? 'border-primary bg-primary/10'
                  : 'border-white/20 bg-white/8 hover:border-white/40 hover:bg-white/15'
              }`}
            >
              <button
                onClick={() => onSelect(deck)}
                className="flex-1 text-left px-4 py-3 flex items-start gap-3 min-w-0"
              >
                <div className={`hidden sm:flex w-8 h-8 rounded-lg items-center justify-center text-lg shrink-0 mt-0.5 ${
                  selected?.id === deck.id ? 'bg-primary/20' : 'bg-white/10'
                }`}>
                  📚
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-white leading-snug">{deck.titre}</div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {deck.education_level && (
                      <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-0.5 rounded-full font-medium">
                        {deck.education_level}
                      </span>
                    )}
                    {deck.matiere && (
                      <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded-full font-medium">
                        {deck.matiere}
                      </span>
                    )}
                  </div>
                  {deck.keywords?.length > 0 && (
                    <div className="text-xs text-white/50 mt-1">
                      {deck.keywords.map(k => `#${k}`).join(' ')}
                    </div>
                  )}
                </div>
              </button>
              <div className="flex items-center gap-1 pr-3 shrink-0">
                <a
                  href={`/host/${tab === 'mine' ? 'decks' : 'discover'}/${deck.id}/questions`}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Voir ce quiz"
                  className="p-1.5 rounded-lg text-white/30 hover:text-white hover:bg-white/10 transition opacity-0 group-hover/item:opacity-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </a>
                {showChevron ? (
                  <svg className="w-5 h-5 text-white/30 group-hover/item:text-white/70 transition shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                ) : selected?.id === deck.id ? (
                  <div className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs shrink-0">✓</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Paramètres de partie (dark) ──────────────────────────────
function StepConfig({ config, onChange, selectedDeck }) {
  const set = (k, v) => onChange({ ...config, [k]: v })

  const nbCases = config.nb_cases ?? selectedDeck?.nb_cases ?? 30
  const nbBonus = config.nb_cases_bonus ?? Math.max(1, Math.round(nbCases * BONUS_RATIO))

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-white/80">Longueur du circuit</label>
          <span className="text-sm font-semibold text-white">
            {nbCases} cases · {nbBonus} bonus
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-white/40 w-6 text-center">{MIN_CIRCUIT_CASES}</span>
          <input
            type="range"
            min={MIN_CIRCUIT_CASES}
            max={MAX_CIRCUIT_CASES}
            value={nbCases}
            onChange={e => {
              const n = parseInt(e.target.value)
              onChange({ ...config, nb_cases: n, nb_cases_bonus: Math.max(1, Math.round(n * BONUS_RATIO)) })
            }}
            className="flex-1 accent-blue-500"
          />
          <span className="text-xs text-white/40 w-6 text-center">{MAX_CIRCUIT_CASES}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">
            Timer max <span className="text-white/40">(s)</span>
          </label>
          <input
            type="number" min={5} max={120}
            value={config.timer_max}
            onChange={e => set('timer_max', parseInt(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-white/30"
          />
          <p className="text-xs text-white/40 mt-1">Durée maximum pour répondre à 1 question</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-white/80 mb-1">
            Timer min <span className="text-white/40">(s)</span>
          </label>
          <input
            type="number" min={1} max={30}
            value={config.timer_min}
            onChange={e => set('timer_min', parseInt(e.target.value))}
            className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-white/30"
          />
          <p className="text-xs text-white/40 mt-1">Délai minimum avant de passer à la question suivante</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">Fin de partie</label>
        <div className="flex gap-3">
          {[
            { value: 'all_finish',   label: 'Tous arrivent',  desc: 'Attend que tous les joueurs finissent' },
            { value: 'first_finish', label: 'Premier arrivé', desc: 'S\'arrête dès que le 1er joueur finit' },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => set('mode_fin', opt.value)}
              className={`flex-1 rounded-xl border-2 px-3 py-2.5 text-left transition ${
                config.mode_fin === opt.value
                  ? 'border-primary bg-primary/10'
                  : 'border-white/15 hover:border-white/30 hover:bg-white/5'
              }`}
            >
              <div className="font-medium text-sm text-white">{opt.label}</div>
              <div className="text-xs text-white/50 mt-0.5">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── Sélecteur de véhicule (solo) ─────────────────────────────
function SoloVehiclePicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-4 gap-2 p-1">
      {VEHICLE_TYPES.map(v => {
        const selected = value === v.emoji
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => onChange(v.emoji)}
            className={`relative flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all duration-150 active:scale-90 ${
              selected
                ? 'border-white bg-white/25 shadow-lg'
                : 'border-white/20 bg-white/5 hover:bg-white/15'
            }`}
          >
            <TwemojiImg emoji={v.emoji} size={32} />
            <span className="text-white/80 text-xs leading-tight text-center w-full truncate">{v.label}</span>
            {selected && (
              <div className="absolute top-1 right-1 w-4 h-4 bg-white text-primary rounded-full text-xs font-semibold flex items-center justify-center shadow">✓</div>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ── Panneau inline Partie Solo (3 étapes) ────────────────────
function SoloGamePanel({ onClose, onCreated, session }) {
  const [step, setStep]               = useState(1)
  const [selectedDeck, setSelectedDeck] = useState(null)
  const [config, setConfig]           = useState({
    tours:          1,
    timer_max:      GAME_DEFAULTS.TIMER_MAX,
    timer_min:      GAME_DEFAULTS.TIMER_MIN,
    vitesse_active: true,
    palier_v2:      2,
    palier_v3:      5,
  })
  const firstName = session?.user?.user_metadata?.full_name?.split(' ')[0]
    ?? session?.user?.email?.split('@')[0]
    ?? 'Joueur'
  const savedVehicle = localStorage.getItem('solo_vehicle_type')
  const [vehicle, setVehicle] = useState(
    VEHICLE_TYPES.find(v => v.emoji === savedVehicle)?.emoji ?? VEHICLE_TYPES[0].emoji
  )
  const [creating, setCreating] = useState(false)
  const [error, setError]       = useState('')

  function handleSelectDeck(deck) {
    setSelectedDeck(deck)
    const nb = deck.nb_cases ?? 30
    setConfig(c => ({
      ...c,
      timer_max:      deck.timer_max ?? GAME_DEFAULTS.TIMER_MAX,
      timer_min:      deck.timer_min ?? GAME_DEFAULTS.TIMER_MIN,
      nb_cases:       nb,
      nb_cases_bonus: Math.max(1, Math.round(nb * BONUS_RATIO)),
    }))
  }

  async function handleCreate() {
    setCreating(true)
    setError('')
    try {
      const data = await apiFetch('/api/games/solo', {
        method: 'POST',
        body: JSON.stringify({ deck_id: selectedDeck.id, pseudo: firstName, type_vehicule: vehicle, config }),
      })
      localStorage.setItem('solo_vehicle_type', vehicle)
      saveSession(data.code, data.session_token, data.kart.id)
      onCreated(data.code)
    } catch (e) {
      setError(e.message)
      setCreating(false)
    }
  }

  return (
    <div className="w-full max-w-lg flex flex-col bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-2xl">

      {/* En-tête */}
      <div className="flex items-center justify-between px-6 pt-5 pb-4">
        <h2 className="text-white text-xl font-semibold">🏎️ Partie Solo</h2>
        <button
          onClick={onClose}
          className="text-white/50 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition"
          aria-label="Annuler"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Étape 1 — Choix du quiz */}
      {step === 1 && (
        <div className="px-6 pb-4">
          <h3 className="text-white/60 text-xs font-medium uppercase tracking-widest mb-3">Choisir un quiz</h3>
          <StepDeck selected={selectedDeck} onSelect={deck => { handleSelectDeck(deck); setStep(2) }} showChevron />
        </div>
      )}

      {/* Étape 2 — Paramètres simplifiés */}
      {step === 2 && (
        <div className="px-6 pb-4 space-y-4">
          <h3 className="text-white/60 text-xs font-medium uppercase tracking-widest">Paramètres</h3>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-white/80">Longueur du circuit</label>
              <span className="text-sm font-semibold text-white">
                {config.nb_cases ?? 30} cases · {config.nb_cases_bonus ?? Math.max(1, Math.round((config.nb_cases ?? 30) * BONUS_RATIO))} bonus
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/40 w-6 text-center">{MIN_CIRCUIT_CASES}</span>
              <input
                type="range"
                min={MIN_CIRCUIT_CASES}
                max={MAX_CIRCUIT_CASES}
                value={config.nb_cases ?? 30}
                onChange={e => {
                  const n = parseInt(e.target.value)
                  setConfig(c => ({ ...c, nb_cases: n, nb_cases_bonus: Math.max(1, Math.round(n * BONUS_RATIO)) }))
                }}
                className="flex-1 accent-blue-500"
              />
              <span className="text-xs text-white/40 w-6 text-center">{MAX_CIRCUIT_CASES}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Timer max <span className="text-white/40">(s)</span>
              </label>
              <input
                type="number" min={5} max={120}
                value={config.timer_max}
                onChange={e => setConfig(c => ({ ...c, timer_max: parseInt(e.target.value) }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-white/30"
              />
              <p className="text-xs text-white/40 mt-1">Durée max pour répondre</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">
                Timer min <span className="text-white/40">(s)</span>
              </label>
              <input
                type="number" min={1} max={30}
                value={config.timer_min}
                onChange={e => setConfig(c => ({ ...c, timer_min: parseInt(e.target.value) }))}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-white/30"
              />
              <p className="text-xs text-white/40 mt-1">Délai minimum avant de passer</p>
            </div>
          </div>
        </div>
      )}

      {/* Étape 3 — Véhicule */}
      {step === 3 && (
        <div className="px-6 pb-2">
          <h3 className="text-white/60 text-xs font-medium uppercase tracking-widest mb-3">Ton pilote</h3>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-white/80">Ton véhicule</label>
            <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2.5 py-1">
              <TwemojiImg emoji={vehicle} size={14} />
              <span className="text-white text-xs font-medium">{VEHICLE_TYPES.find(v => v.emoji === vehicle)?.label}</span>
            </div>
          </div>
          <SoloVehiclePicker value={vehicle} onChange={setVehicle} />
        </div>
      )}

      {error && (
        <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mx-6 mb-2">{error}</p>
      )}

      {/* Actions — étapes 2 et 3 uniquement */}
      {step > 1 && (
        <div className="flex justify-between items-center px-6 py-4 border-t border-white/10">
          <button
            onClick={() => setStep(s => s - 1)}
            className="px-4 py-2 text-white/60 hover:text-white transition"
          >
            ← Retour
          </button>
          {step < 3 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              className="btn-primary px-6 py-2"
            >
              Suivant →
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={creating}
              className="btn-primary px-6 py-2 disabled:opacity-60"
            >
              {creating ? 'Création…' : '🚀 Lancer !'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Panneau inline Rejoindre une partie (2 étapes) ───────────
function JoinPanel({ onClose, onJoined, session }) {
  const [step, setStep] = useState(1)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [codeLoading, setCodeLoading] = useState(false)
  const defaultPseudo = session?.user?.user_metadata?.full_name?.split(' ')[0]
    ?? session?.user?.email?.split('@')[0]
    ?? ''
  const [pseudo, setPseudo] = useState(defaultPseudo)
  const savedVehicle = localStorage.getItem('join_vehicle_type')
  const [vehicle, setVehicle] = useState(
    VEHICLE_TYPES.find(v => v.emoji === savedVehicle)?.emoji ?? VEHICLE_TYPES[0].emoji
  )
  const [joinError, setJoinError] = useState('')
  const [joining, setJoining] = useState(false)

  async function handleCodeSubmit(e) {
    e.preventDefault()
    const clean = code.trim().toUpperCase()
    if (clean.length !== 6) return setCodeError('Le code fait 6 caractères.')
    setCodeLoading(true)
    setCodeError('')
    try {
      await apiFetch(`/api/games/${clean}`)
      setCode(clean)
      setStep(2)
    } catch {
      setCodeError('Partie introuvable. Vérifie le code.')
    }
    setCodeLoading(false)
  }

  async function handleJoin() {
    if (!pseudo.trim()) return setJoinError('Entre un pseudo.')
    setJoining(true)
    setJoinError('')
    try {
      const { kart, session_token } = await apiFetch(`/api/karts/${code}/join`, {
        method: 'POST',
        body: JSON.stringify({ pseudo: pseudo.trim(), type_vehicule: vehicle }),
      })
      localStorage.setItem('join_vehicle_type', vehicle)
      saveSession(code, session_token, kart.id)
      onJoined(code)
    } catch (err) {
      setJoinError(err.message)
      setJoining(false)
    }
  }

  return (
    <div className="flex-1 max-w-lg mx-auto flex flex-col bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
      {/* En-tête */}
      <div className="flex items-center justify-between px-6 pt-5 pb-4 shrink-0">
        <h2 className="text-white text-xl font-semibold">🎮 Rejoindre une partie</h2>
        <button
          onClick={onClose}
          className="text-white/50 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition"
          aria-label="Annuler"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Étape 1 — Code */}
      {step === 1 && (
        <form onSubmit={handleCodeSubmit} className="flex-1 flex flex-col justify-center px-6 pb-4 gap-4">
          <p className="text-white/60 text-sm text-center">Entre le code affiché par l'hôte de la partie</p>
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase().slice(0, 6))}
            placeholder="ABC123"
            className="w-full text-center text-3xl font-mono font-semibold tracking-[0.3em] border-2 border-white/30 bg-white/10 text-white placeholder-white/40 rounded-2xl px-4 py-4 focus:outline-none focus:border-white uppercase"
            maxLength={6}
            autoComplete="off"
            autoCapitalize="characters"
            autoFocus
          />
          {codeError && <p className="text-red-400 text-sm text-center">{codeError}</p>}
          <div className="flex justify-between items-center pt-2 border-t border-white/10 mt-auto">
            <button type="button" onClick={onClose} className="px-4 py-2 text-white/60 hover:text-white transition">
              Annuler
            </button>
            <button type="submit" disabled={codeLoading || code.length !== 6} className="btn-primary px-6 py-2 disabled:opacity-50">
              {codeLoading ? '…' : 'Entrer →'}
            </button>
          </div>
        </form>
      )}

      {/* Étape 2 — Profil */}
      {step === 2 && (
        <div className="flex-1 flex flex-col min-h-0 px-6 pb-2">
          <h3 className="text-white/60 text-xs font-medium uppercase tracking-widest mb-3 shrink-0">Ton pilote</h3>
          <input
            value={pseudo}
            onChange={e => setPseudo(e.target.value.slice(0, 20))}
            placeholder="Ton pseudo…"
            autoFocus
            className="shrink-0 w-full text-center text-xl font-semibold border-2 border-white/30 bg-white/10 text-white placeholder-white/40 rounded-2xl px-4 py-3 mb-3 focus:outline-none focus:border-white"
            maxLength={20}
          />
          <div className="flex items-center justify-between mb-2 shrink-0">
            <label className="text-sm font-medium text-white/80">Ton véhicule</label>
            <div className="flex items-center gap-1.5 bg-white/20 rounded-full px-2.5 py-1">
              <TwemojiImg emoji={vehicle} size={14} />
              <span className="text-white text-xs font-medium">{VEHICLE_TYPES.find(v => v.emoji === vehicle)?.label}</span>
            </div>
          </div>
          <SoloVehiclePicker value={vehicle} onChange={setVehicle} />
          {joinError && (
            <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mt-2 shrink-0">{joinError}</p>
          )}
          <div className="flex justify-between items-center pt-3 border-t border-white/10 mt-2 shrink-0">
            <button onClick={() => setStep(1)} className="px-4 py-2 text-white/60 hover:text-white transition">
              ← Retour
            </button>
            <button onClick={handleJoin} disabled={joining || !pseudo.trim()} className="btn-primary px-6 py-2 disabled:opacity-60">
              {joining ? 'Connexion…' : '🚦 Rejoindre !'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Panneau inline de création de partie (2 étapes) ──────────
function NewGamePanel({ onClose, onCreated }) {
  const [step, setStep] = useState(1)
  const [selectedDeck, setSelectedDeck] = useState(null)
  const [config, setConfig] = useState({
    tours:          1,
    timer_max:      GAME_DEFAULTS.TIMER_MAX,
    timer_min:      GAME_DEFAULTS.TIMER_MIN,
    vitesse_active: true,
    palier_v2:      2,
    palier_v3:      5,
    mode_fin:       'all_finish',
    max_joueurs:    GAME_DEFAULTS.MAX_PLAYERS,
  })
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  function handleSelectDeck(deck) {
    setSelectedDeck(deck)
    const nb = deck.nb_cases ?? 30
    setConfig(c => ({
      ...c,
      timer_max:      deck.timer_max ?? GAME_DEFAULTS.TIMER_MAX,
      timer_min:      deck.timer_min ?? GAME_DEFAULTS.TIMER_MIN,
      nb_cases:       nb,
      nb_cases_bonus: Math.max(1, Math.round(nb * BONUS_RATIO)),
    }))
  }

  async function handleCreate() {
    setCreating(true)
    setError('')
    try {
      const data = await apiFetch('/api/games', {
        method: 'POST',
        body: JSON.stringify({
          deck_id: selectedDeck.id,
          config,
        }),
      })
      onCreated(data.code)
    } catch (e) {
      setError(e.message)
    }
    setCreating(false)
  }

  return (
    <div className="w-full max-w-lg flex flex-col bg-gray-900/95 backdrop-blur-sm border border-white/10 rounded-2xl">
      {/* En-tête */}
      <div className="flex items-center justify-between px-6 pt-5 pb-4">
        <h2 className="text-white text-xl font-semibold">🏁 Nouvelle partie</h2>
        <button
          onClick={onClose}
          className="text-white/50 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition"
          aria-label="Annuler"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Étape 1 — Choix du quiz */}
      {step === 1 && (
        <div className="px-6 pb-4">
          <h3 className="text-white/60 text-xs font-medium uppercase tracking-widest mb-3">Choisir un quiz</h3>
          <StepDeck
            selected={selectedDeck}
            onSelect={deck => { handleSelectDeck(deck); setStep(2) }}
            showChevron
          />
        </div>
      )}

      {/* Étape 2 — Paramètres */}
      {step === 2 && (
        <div className="px-6 pb-4">
          <h3 className="text-white/60 text-xs font-medium uppercase tracking-widest mb-3">Paramètres de la partie</h3>
          <StepConfig config={config} onChange={setConfig} selectedDeck={selectedDeck} />
        </div>
      )}

      {error && (
        <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mx-6 mb-2">{error}</p>
      )}

      {/* Actions — uniquement à l'étape 2 */}
      {step === 2 && (
        <div className="flex justify-between items-center px-6 py-4 border-t border-white/10">
          <button
            onClick={() => setStep(1)}
            className="px-4 py-2 text-white/60 hover:text-white transition"
          >
            ← Retour
          </button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="btn-primary px-6 py-2 disabled:opacity-60"
          >
            {creating ? 'Création…' : 'Créer la partie'}
          </button>
        </div>
      )}
    </div>
  )
}

// ── Carte partie en cours ────────────────────────────────────
function ActiveGameCard({ game, onResume }) {
  const statusLabel = { waiting: 'En attente', playing: 'En cours', paused: 'Pausée' }
  const statusColor = { waiting: 'text-amber-400 bg-amber-400/20', playing: 'text-green-400 bg-green-400/20', paused: 'text-orange-400 bg-orange-400/20' }

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-4xl">🎮</span>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor[game.statut] ?? 'text-white/60 bg-white/10'}`}>
          ● {statusLabel[game.statut] ?? game.statut}
        </span>
      </div>
      <div>
        <h2 className="font-semibold text-white text-lg">{game.decks?.titre ?? '—'}</h2>
        <div className="flex items-center gap-3 mt-1 text-sm text-white/60 flex-wrap">
          {game.decks?.themes?.label && <span>🎨 {game.decks.themes.label}</span>}
          {game.decks?.difficulty && <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full text-white/70">{game.decks.difficulty}</span>}
          <span className="text-xs text-white/40 font-mono tracking-wider">#{game.code}</span>
        </div>
      </div>
      <button
        onClick={onResume}
        className="btn-primary px-5 py-2.5 self-start mt-auto"
      >
        Reprendre la partie →
      </button>
    </div>
  )
}

// ── Carte partie historique ──────────────────────────────────
function HistoricGameCard({ game, onView }) {
  const date = game.termine_le
    ? new Date(game.termine_le).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—'

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-gray-900/90 transition">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white/50 bg-white/10">
              Terminée
            </span>
            <span className="text-xs text-white/40">Code : <strong className="font-mono tracking-wider">{game.code}</strong></span>
            <span className="text-xs text-white/40">{date}</span>
          </div>
          <h3 className="font-medium text-white mt-1 truncate">{game.decks?.titre ?? '—'}</h3>
          <div className="flex items-center gap-3 mt-0.5 text-sm text-white/60 flex-wrap">
            {game.decks?.themes?.label && <span>🎨 {game.decks.themes.label}</span>}
            {game.decks?.difficulty && <span className="text-xs bg-white/10 px-2 py-0.5 rounded-full text-white/70">{game.decks.difficulty}</span>}
          </div>
        </div>
        <button
          onClick={onView}
          className="text-sm font-medium text-white/70 hover:text-white transition px-4 py-2 rounded-lg border border-white/20 hover:bg-white/10 shrink-0"
        >
          Voir →
        </button>
      </div>
    </div>
  )
}

// ── Page principale ──────────────────────────────────────────
export default function HomePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [session, setSession] = useState(undefined)
  const [role, setRole] = useState(null)
  const [tags, setTags] = useState([])
  const [activeGame, setActiveGame] = useState(undefined) // undefined = loading, null = aucune
  const [createOpen, setCreateOpen] = useState(false)
  const [soloOpen, setSoloOpen]     = useState(false)
  const [joinOpen, setJoinOpen]     = useState(false)
  const [loadingGame, setLoadingGame] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [historicGames, setHistoricGames] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [historyError, setHistoryError] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [feedbackPending, setFeedbackPending] = useState(null)
  const [sharedQuizzes, setSharedQuizzes] = useState([])
  const [shareToast, setShareToast] = useState('')

  const loadedUserRef = useRef(null)
  const { isDismissed, dismiss } = useContentPrefs(session?.user?.id)

  // Toast depuis navigation (ex: ShareRedirectPage)
  useEffect(() => {
    if (location.state?.toastMessage) {
      setShareToast(location.state.toastMessage)
      setTimeout(() => setShareToast(''), 4000)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state]) // eslint-disable-line react-hooks/exhaustive-deps

  // Gestion de la session — onAuthStateChange gère aussi les callbacks OAuth (INITIAL_SESSION)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => subscription.unsubscribe()
  }, [])

  // Redirect si non connecté
  useEffect(() => {
    if (session === null) navigate('/login', { replace: true })
  }, [session, navigate])

  // Chargement du rôle utilisateur
  useEffect(() => {
    if (!session) return
    supabase
      .from('profiles')
      .select('role, tags')
      .eq('id', session.user.id)
      .single()
      .then(({ data }) => {
        setRole(data?.role ?? null)
        setTags(data?.tags ?? [])
      })
  }, [session])

  // Chargement de la partie active
  function refreshActiveGame() {
    setLoadingGame(true)
    apiFetch('/api/games/mine')
      .then(data => setActiveGame(data))
      .catch(() => setActiveGame(null))
      .finally(() => setLoadingGame(false))
  }

  useEffect(() => {
    if (!session) return
    const uid = session.user.id
    if (loadedUserRef.current === uid) return
    loadedUserRef.current = uid
    refreshActiveGame()
    apiFetch('/api/feedbacks/host/pending')
      .then(data => { if (data?.should_show) setFeedbackPending(data) })
      .catch(() => {})
    // Charge les quiz partagés (le rattachement éventuel est déjà fait dans AuthCallbackPage)
    apiFetch('/api/share')
      .then(data => setSharedQuizzes(data ?? []))
      .catch(() => {})
  }, [session])

  function loadHistory() {
    setLoadingHistory(true)
    setHistoryError(false)
    apiFetch('/api/games/history')
      .then(data => setHistoricGames(data ?? []))
      .catch(() => setHistoryError(true))
      .finally(() => setLoadingHistory(false))
  }

  function openHistory() {
    setShowHistory(true)
    setDrawerOpen(false)
    if (historicGames.length === 0) loadHistory()
  }

  function closeHistory() {
    setShowHistory(false)
  }

  async function handleLogout() {
    setDrawerOpen(false)
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  function handleGameCreated(code) {
    setCreateOpen(false)
    navigate(`/host/${code}`)
  }

  function handleSoloCreated(code) {
    setSoloOpen(false)
    navigate(`/solo/${code}`)
  }

  function handleJoined(code) {
    setJoinOpen(false)
    navigate(`/lobby/${code}`)
  }

  if (session === undefined || session === null) return null

  const userEmail = session.user?.email ?? ''
  const bgStyle = { backgroundImage: `url(${podiumBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }
  const unplayedCount = sharedQuizzes.filter(q => !q.played).length

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden" style={bgStyle}>

      {/* Toast partage */}
      {shareToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-full shadow-lg text-sm font-medium whitespace-nowrap">
          ✅ {shareToast}
        </div>
      )}

      {/* ── Header ───────────────────────────────────────────── */}
      <header className="bg-gray-900/95 backdrop-blur-sm px-4 py-3 flex items-center justify-between gap-4 shrink-0 border-b border-white/10">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-8 h-8 rounded-lg shrink-0" />
          <span className="font-brand text-lg text-white">KartQuiz</span>
        </div>

        {/* Navigation desktop (md+) */}
        <nav className="hidden md:flex items-center gap-2 text-sm">
          <span className="text-white/40 text-sm hidden lg:block mr-1">{userEmail}</span>
          {tags.includes('early_adopter') && (
            <span className="inline-flex items-center gap-1 text-xs bg-amber-400/15 text-amber-300 font-semibold px-2.5 py-0.5 rounded-full border border-amber-400/20 hidden lg:inline-flex">
              ⭐ Early Adopter
            </span>
          )}
          <button
            onClick={showHistory ? closeHistory : openHistory}
            className={`px-3 py-1.5 rounded-lg transition ${showHistory ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
          >
            🕐 Historique
          </button>
          <Link
            to="/host/decks"
            className="text-white/70 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition"
          >
            📚 Mes quiz
          </Link>
          <Link
            to="/host/discover"
            className="text-white/70 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition"
          >
            🌍 Découvrir
          </Link>
          {role === 'admin' && (
            <Link
              to="/admin"
              className="text-white/70 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition"
            >
              ⚙️ Admin
            </Link>
          )}
          <Link
            to="/profile"
            className="text-white/70 hover:text-white hover:bg-white/10 px-3 py-1.5 rounded-lg transition"
          >
            👤 Mon profil
          </Link>
          <button
            onClick={handleLogout}
            className="text-white/70 hover:text-red-300 hover:bg-red-500/20 px-3 py-1.5 rounded-lg transition"
          >
            Déconnexion
          </button>
        </nav>

        {/* Hamburger mobile (< md) */}
        <button
          onClick={() => setDrawerOpen(true)}
          className="md:hidden text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition"
          aria-label="Menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </header>

      {/* ── Drawer mobile ────────────────────────────────────── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Panel */}
          <div className="absolute inset-y-0 right-0 w-64 bg-gray-900 border-l border-white/10 flex flex-col shadow-2xl">
            {/* En-tête drawer */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-7 h-7 rounded-lg" />
                <span className="font-brand text-white">KartQuiz</span>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="text-white/50 hover:text-white p-1 rounded-lg hover:bg-white/10 transition"
                aria-label="Fermer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Email */}
            <div className="px-4 py-3 border-b border-white/10">
              <p className="text-white/50 text-sm truncate">{userEmail}</p>
              {tags.includes('early_adopter') && (
                <span className="inline-flex items-center gap-1 text-xs bg-amber-400/15 text-amber-300 font-semibold px-2 py-0.5 rounded-full border border-amber-400/20 mt-1.5">
                  ⭐ Early Adopter
                </span>
              )}
            </div>

            {/* Items nav */}
            <nav className="flex-1 flex flex-col py-2">
              <button
                onClick={showHistory ? () => { closeHistory(); setDrawerOpen(false) } : openHistory}
                className="flex items-center gap-3 px-4 min-h-[44px] text-white/80 hover:text-white hover:bg-white/10 transition text-sm font-medium"
              >
                <span className="text-lg">🕐</span>
                <span>Historique</span>
              </button>
              <Link
                to="/host/decks"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-3 px-4 min-h-[44px] text-white/80 hover:text-white hover:bg-white/10 transition text-sm font-medium"
              >
                <span className="text-lg">📚</span>
                <span>Mes quiz</span>
              </Link>
              <Link
                to="/host/discover"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-3 px-4 min-h-[44px] text-white/80 hover:text-white hover:bg-white/10 transition text-sm font-medium"
              >
                <span className="text-lg">🌍</span>
                <span>Découvrir</span>
              </Link>
              {role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center gap-3 px-4 min-h-[44px] text-white/80 hover:text-white hover:bg-white/10 transition text-sm font-medium"
                >
                  <span className="text-lg">⚙️</span>
                  <span>Admin</span>
                </Link>
              )}
              <Link
                to="/profile"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-3 px-4 min-h-[44px] text-white/80 hover:text-white hover:bg-white/10 transition text-sm font-medium"
              >
                <span className="text-lg">👤</span>
                <span>Mon profil</span>
              </Link>
            </nav>

            {/* Déconnexion */}
            <div className="border-t border-white/10 p-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 min-h-[44px] text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-xl transition text-sm font-medium"
              >
                <span className="text-lg">🚪</span>
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Contenu principal ────────────────────────────────── */}
      <main className={`flex-1 ${joinOpen ? 'overflow-hidden' : 'overflow-y-auto'}`}>

        {soloOpen && (
          <div className="p-4 flex justify-center">
            <SoloGamePanel
              onClose={() => setSoloOpen(false)}
              onCreated={handleSoloCreated}
              session={session}
            />
          </div>
        )}

        {joinOpen && (
          <div className="h-full p-4 flex">
            <JoinPanel
              onClose={() => setJoinOpen(false)}
              onJoined={handleJoined}
              session={session}
            />
          </div>
        )}

        {createOpen && (
          <div className="p-4 flex justify-center">
            <NewGamePanel
              onClose={() => setCreateOpen(false)}
              onCreated={handleGameCreated}
            />
          </div>
        )}

        {!soloOpen && !joinOpen && !createOpen && (
        <div className="p-4 w-full max-w-3xl mx-auto md:min-h-full md:flex md:flex-col">

          {/* Historique */}
          {showHistory ? (
            <div className="space-y-4">
              <div>
                <button
                  onClick={closeHistory}
                  className="btn-primary px-4 py-2"
                >
                  ← Retour
                </button>
              </div>
              {loadingHistory ? (
                <div className="flex justify-center py-10">
                  <div className="flex flex-col items-center gap-3 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-8">
                    <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    <p className="text-white/70 text-sm">Chargement…</p>
                  </div>
                </div>
              ) : historyError ? (
                <div className="flex justify-center py-12">
                  <div className="flex flex-col items-center gap-3 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-8 text-white/70">
                    <div className="text-4xl">⚠️</div>
                    <p className="text-sm text-center">Impossible de charger l'historique.<br />Vérifiez votre connexion ou reconnectez-vous.</p>
                    <button onClick={loadHistory} className="mt-2 px-4 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm transition">
                      Réessayer
                    </button>
                  </div>
                </div>
              ) : historicGames.length === 0 ? (
                <div className="flex justify-center py-12">
                  <div className="flex flex-col items-center gap-3 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-8 text-white/70">
                    <div className="text-4xl">📭</div>
                    <p className="text-sm">Aucune course terminée pour l'instant.</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {historicGames.map(game => (
                    <HistoricGameCard
                      key={game.id}
                      game={game}
                      onView={() => navigate(`/stats/${game.code}`)}
                    />
                  ))}
                </div>
              )}
            </div>

          /* Chargement */
          ) : loadingGame ? (
            <div className="flex justify-center py-16">
              <div className="flex flex-col items-center gap-3 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-8">
                <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <p className="text-white/70 text-sm">Chargement…</p>
              </div>
            </div>

          /* Partie en cours — bloc unique */
          ) : activeGame ? (
            <div className="pt-[35vh] md:pt-0 md:flex-1 md:flex md:flex-col md:justify-center">
              <div className="max-w-sm mx-auto">
                <ActiveGameCard
                  game={activeGame}
                  onResume={() => navigate(activeGame.config?.solo ? `/solo/${activeGame.code}` : `/host/${activeGame.code}`)}
                />
              </div>
            </div>

          /* État vide — prêt à jouer */
          ) : (
            <div className="pt-[35vh] md:pt-0 md:flex-1 md:flex md:flex-col md:justify-center">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* Carte Bienvenue */}
              {!isDismissed('welcome_video') && (
                <WelcomeVideoCard onDismiss={() => dismiss('welcome_video')} />
              )}

              {/* Carte 1 — Lancer une partie */}
              <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
                <div className="text-4xl">🏁</div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Prêt à jouer ?</h2>
                  <p className="text-white/60 text-sm mt-1">Lance une nouvelle partie, embarque ta classe ou ta tribue.</p>
                </div>
                <button
                  onClick={() => setCreateOpen(true)}
                  className="btn-primary px-5 py-2.5 self-start mt-auto"
                >
                  Lancer une partie
                </button>
              </div>

              {/* Carte 2 — Partie Solo */}
              <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
                <div className="text-4xl">🏎️</div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Jouer en Solo</h2>
                  <p className="text-white/60 text-sm mt-1">Révise seul, en t'entraînant sur un quiz existant.</p>
                </div>
                <button
                  onClick={() => setSoloOpen(true)}
                  className="btn-primary px-5 py-2.5 self-start mt-auto"
                >
                  Mode Solo
                </button>
              </div>

              {/* Carte 3 — Rejoindre une partie */}
              <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
                <div className="text-4xl">🎮</div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Rejoindre une partie</h2>
                  <p className="text-white/60 text-sm mt-1">Entre dans une partie existante avec un code à 6 caractères.</p>
                </div>
                <button
                  onClick={() => setJoinOpen(true)}
                  className="btn-primary px-5 py-2.5 self-start mt-auto"
                >
                  Rejoindre
                </button>
              </div>

              {/* Carte 4 — Mes quiz */}
              <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
                <div className="text-4xl">🎓</div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Ton quiz sur mesure</h2>
                  <p className="text-white/60 text-sm mt-1">Crée et gère tes quiz personnalisés.</p>
                </div>
                <button
                  onClick={() => navigate('/host/decks')}
                  className="btn-primary px-5 py-2.5 self-start mt-auto"
                >
                  Mes quiz
                </button>
              </div>

              {/* Carte Partagés avec moi */}
              {sharedQuizzes.length > 0 && (
                <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6 flex flex-col gap-4">
                  <div className="flex items-start justify-between">
                    <div className="text-4xl">🤝</div>
                    {unplayedCount > 0 && (
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold shrink-0">
                        {unplayedCount}
                      </span>
                    )}
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">Partagés avec moi</h2>
                    <p className="text-white/60 text-sm mt-1">Retrouve les quiz qu'on t'a partagé.</p>
                  </div>
                  <button
                    onClick={() => navigate('/shared-quizzes')}
                    className="btn-primary px-5 py-2.5 self-start mt-auto"
                  >
                    Voir les quiz
                  </button>
                </div>
              )}

            {/* Bloc feedback hôte */}
            {feedbackPending && (
              <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xl">⭐</span>
                  <h2 className="text-white font-semibold text-base">Ton avis sur KartQuiz</h2>
                </div>
                <FeedbackForm
                  source="hote"
                  partieId={feedbackPending.partie_id}
                  existing={feedbackPending.existing}
                  onDone={() => setFeedbackPending(null)}
                />
              </div>
            )}
            </div>
            </div>
          )}
        </div>
        )}
      </main>
    </div>
  )
}
